import React, { useState, useRef, useCallback } from 'react';
import {
    DndContext,
    closestCenter,
    CollisionDetection,
    useSensors,
    useSensor,
    PointerSensor,
    DragEndEvent,
    DragStartEvent,
    DragOverlay,
    UniqueIdentifier,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { JoinedSessionDetailsItem } from 'queries/groups';

import {
    SortableSessionItem,
    getSessionItemKey,
    SessionDetailsItem,
} from 'components/ui/SortableSessionItem';
import { Button } from 'components/ui/Button';

import {
    Bumper,
    Card,
    CustomizableWidthWrapper,
    HorizontalFlexWrapper,
} from 'styles/shared';
import { Fonts, spacing } from 'styles/theme';

type Container = 'pool' | 'list' | 'bank' | null;

interface ReorderSessionsProps {
    initialPoolItems: JoinedSessionDetailsItem[];
    initialListItems: JoinedSessionDetailsItem[];
    initialBankedItems: JoinedSessionDetailsItem[];
    onSave: (
        updatedList: JoinedSessionDetailsItem[],
        updatedBank: JoinedSessionDetailsItem[]
    ) => void;
    currentSessionNumber: number;
    shouldShowDisclaimer?: boolean;
}

export const ReorderSessions = ({
    initialPoolItems,
    initialListItems,
    initialBankedItems,
    onSave,
    currentSessionNumber,
    shouldShowDisclaimer,
}: ReorderSessionsProps) => {
    const listContainerRef = useRef<HTMLDivElement>(null);

    const [poolItems, setPoolItems] = useState<SessionDetailsItem[]>(
        initialPoolItems.map((item, idx) => ({
            ...item,
            sessionNumber: idx + 1,
            isBanked: false,
        }))
    );
    const [listItems, setListItems] = useState<SessionDetailsItem[]>(
        initialListItems.map((item, idx) => ({
            ...item,
            sessionNumber: idx + 1,
            isBanked: false,
        }))
    );
    const [bankedItems, setBankedItems] = useState<SessionDetailsItem[]>(
        initialBankedItems.map((item, idx) => ({
            ...item,
            sessionNumber: idx + 1,
            isBanked: true,
        }))
    );
    const [activeItem, setActiveItem] = useState<SessionDetailsItem | null>(
        null
    );
    const [isModified, setIsModified] = useState(false);
    const listCapacity = useRef(initialListItems.length);

    const sensors = useSensors(useSensor(PointerSensor));

    const findContainer = useCallback(
        (key: UniqueIdentifier | null): Container => {
            if (!key) {
                return null;
            } else if (listItems.some((i) => getSessionItemKey(i) === key)) {
                return 'list';
            } else if (poolItems.some((i) => getSessionItemKey(i) === key)) {
                return 'pool';
            } else if (bankedItems.some((i) => getSessionItemKey(i) === key)) {
                return 'bank';
            }
            return null;
        },
        [listItems, poolItems, bankedItems]
    );

    const collisionDetection: CollisionDetection = useCallback(
        (args) => {
            const {
                active,
                droppableRects,
                droppableContainers,
                collisionRect,
                pointerCoordinates,
            } = args;
            if (findContainer(active.id) === 'list') {
                const ids = listItems.map((i) => getSessionItemKey(i));
                const filteredContainers = droppableContainers.filter((c) =>
                    ids.includes(c.id as string)
                );
                const filteredRects = new Map(
                    [...droppableRects].filter(([key]) =>
                        ids.includes(key as string)
                    )
                );
                return closestCenter({
                    active,
                    droppableContainers: filteredContainers,
                    droppableRects: filteredRects,
                    collisionRect,
                    pointerCoordinates,
                });
            }
            return closestCenter(args);
        },
        [findContainer, listItems]
    );

    const handleDragStart = useCallback(
        ({ active }: DragStartEvent) => {
            const all = [...listItems, ...poolItems, ...bankedItems];
            const found = all.find((i) => getSessionItemKey(i) === active.id);
            setActiveItem(found || null);
        },
        [listItems, poolItems, bankedItems]
    );

    const handleDragCancel = useCallback(() => {
        setActiveItem(null);
    }, []);

    const handleDragEnd = useCallback(
        ({ active, over }: DragEndEvent) => {
            if (!activeItem || over == null) {
                return;
            }
            const from = findContainer(active.id);
            const to = findContainer(over.id);

            if (from === 'list') {
                if (to !== 'list') {
                    setActiveItem(null);
                    return;
                }
                setListItems((items) => {
                    const oldIndex = items.findIndex(
                        (i) => getSessionItemKey(i) === active.id
                    );
                    const newIndex = items.findIndex(
                        (i) => getSessionItemKey(i) === over.id
                    );
                    return arrayMove(items, oldIndex, newIndex);
                });
            }

            if (to === 'list' && (from === 'pool' || from === 'bank')) {
                if (from === 'pool') {
                    setPoolItems((prevPoolItems) => {
                        const newPoolItems = structuredClone(prevPoolItems);
                        const activePoolItem = prevPoolItems.find(
                            (poolItem) =>
                                getSessionItemKey(poolItem) === active.id
                        );
                        const activePoolItemIndex = prevPoolItems.findIndex(
                            (poolItem) =>
                                getSessionItemKey(poolItem) === active.id
                        );
                        if (activePoolItem == null) {
                            return prevPoolItems;
                        }
                        newPoolItems[activePoolItemIndex] = {
                            ...activePoolItem,
                            sessionNumber: activePoolItem.sessionNumber + 1,
                        };
                        return newPoolItems;
                    });
                }
                if (from === 'bank') {
                    setBankedItems((b) =>
                        b.filter((i) => getSessionItemKey(i) !== active.id)
                    );
                }
                setListItems((prevListItems) => {
                    const newListItems = [...prevListItems];
                    const replacedItemIndex = newListItems.findIndex(
                        (listItem) => getSessionItemKey(listItem) === over.id
                    );
                    newListItems.splice(
                        replacedItemIndex >= 0
                            ? replacedItemIndex
                            : newListItems.length,
                        0,
                        activeItem
                    );
                    if (newListItems.length > listCapacity.current) {
                        const bumpedListItem = newListItems.pop();
                        if (
                            bumpedListItem != null &&
                            bumpedListItem.is_repeatable !== true
                        ) {
                            setBankedItems((prevBankedItems) =>
                                prevBankedItems.some(
                                    (bankedItem) =>
                                        getSessionItemKey(bankedItem) ===
                                        getSessionItemKey(bumpedListItem)
                                )
                                    ? prevBankedItems
                                    : [...prevBankedItems, bumpedListItem]
                            );
                        }
                    }
                    return newListItems;
                });
            }

            setActiveItem(null);
            setIsModified(true);
        },
        [activeItem, findContainer]
    );

    const handleReset = useCallback(() => {
        setListItems(
            initialListItems.map((item, idx) => ({
                ...item,
                sessionNumber: idx + 1,
                isBanked: false,
            }))
        );
        setBankedItems(
            initialBankedItems.map((item, idx) => ({
                ...item,
                sessionNumber: idx + 1,
                isBanked: true,
            }))
        );
        setIsModified(false);
        setActiveItem(null);
    }, [initialListItems, initialBankedItems]);

    const handleSave = useCallback(() => {
        onSave(listItems, bankedItems);
        setIsModified(false);
    }, [listItems, bankedItems, onSave]);

    return (
        <>
            <DndContext
                sensors={sensors}
                collisionDetection={collisionDetection}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
            >
                {/* TODO: replace inline styles */}
                <div style={{ display: 'flex' }}>
                    <CustomizableWidthWrapper
                        ref={listContainerRef}
                        $width="260px"
                    >
                        <Fonts.Heading7>{`Session list:`}</Fonts.Heading7>
                        <Bumper $minHeight={spacing.xSmall} />
                        <SortableContext
                            items={listItems.map(getSessionItemKey)}
                            strategy={verticalListSortingStrategy}
                        >
                            {listItems.map((item, index) => (
                                <>
                                    <HorizontalFlexWrapper
                                        key={getSessionItemKey(item)}
                                    >
                                        <Fonts.XSmall>{index + 1}</Fonts.XSmall>
                                        <Bumper $minWidth={spacing.xSmall} />
                                        <CustomizableWidthWrapper $width="260px">
                                            {index ===
                                            currentSessionNumber - 1 ? (
                                                <Fonts.XxSmall>{`Today's Session:`}</Fonts.XxSmall>
                                            ) : null}
                                            <SortableSessionItem
                                                item={item}
                                                transparent={
                                                    getSessionItemKey(item) ===
                                                    getSessionItemKey(
                                                        activeItem
                                                    )
                                                }
                                                isHighlighted={
                                                    index ===
                                                    currentSessionNumber - 1
                                                }
                                                isDimmed={
                                                    index <
                                                    currentSessionNumber - 1
                                                }
                                            />
                                        </CustomizableWidthWrapper>
                                    </HorizontalFlexWrapper>
                                    <Bumper $minHeight={spacing.xSmall} />
                                </>
                            ))}
                        </SortableContext>
                    </CustomizableWidthWrapper>
                    <Bumper $minWidth={spacing.xxxLarge} />
                    <div>
                        <CustomizableWidthWrapper $width="260px">
                            <Fonts.XxSmall as="p">
                                {`You can drag in additional Clarity Lab sessions, if needed (note that adding these will push out the last session in your list):`}
                            </Fonts.XxSmall>
                            <SortableContext
                                items={poolItems.map(getSessionItemKey)}
                                strategy={verticalListSortingStrategy}
                            >
                                {poolItems.map((item) => (
                                    <SortableSessionItem
                                        key={getSessionItemKey(item)}
                                        item={item}
                                    />
                                ))}
                            </SortableContext>
                        </CustomizableWidthWrapper>
                        <Bumper $minHeight={spacing.xxLarge} />
                        {bankedItems.length > 0 ? (
                            <CustomizableWidthWrapper $width="260px">
                                <Fonts.XxSmall as="p">
                                    {`These are sessions that were pushed out of your list, which can be dragged back in:`}
                                </Fonts.XxSmall>
                                <SortableContext
                                    items={bankedItems.map(getSessionItemKey)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {bankedItems.map((item) => (
                                        <>
                                            <SortableSessionItem
                                                key={getSessionItemKey(item)}
                                                item={item}
                                                transparent={
                                                    getSessionItemKey(item) ===
                                                    getSessionItemKey(
                                                        activeItem
                                                    )
                                                }
                                            />
                                            <Bumper
                                                $minHeight={spacing.xSmall}
                                            />
                                        </>
                                    ))}
                                </SortableContext>
                            </CustomizableWidthWrapper>
                        ) : null}
                    </div>
                </div>
                <DragOverlay>
                    {activeItem && (
                        <Card
                            // TODO: replace inline styles
                            style={{ cursor: 'grabbing' }}
                            $padding={spacing.xxSmall}
                        >
                            <Fonts.XSmall>{activeItem.title}</Fonts.XSmall>
                        </Card>
                    )}
                </DragOverlay>
            </DndContext>

            {isModified && (
                <>
                    <Bumper />
                    <HorizontalFlexWrapper $isContentSpread>
                        <Button onClick={handleReset} $style="outline">
                            {`Reset`}
                        </Button>
                        <Button onClick={handleSave}>Save Changes</Button>
                    </HorizontalFlexWrapper>
                    {shouldShowDisclaimer && (
                        <>
                            <Bumper $minHeight={spacing.small} />

                            <HorizontalFlexWrapper $isContentSpread>
                                <div />
                                <Fonts.XxSmall>
                                    {`If you press Next without saving, your changes will be lost.`}
                                </Fonts.XxSmall>
                            </HorizontalFlexWrapper>
                        </>
                    )}
                </>
            )}
        </>
    );
};
